import { useState } from "react";

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormHelperText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

import EditingField from "@/components/Luna/tutor/Tabs/Problems/features/EditingField";
import QuestionContent from "@/components/Luna/tutor/Tabs/Problems/features/QuestionContent";
import { Problem, QuestionType } from "@/types";
import { isHtmlContentEmpty } from "@/utils/htmlContentStyle";

interface EditProblemProps {
  problem: Problem;
  index: number;
  onUpdate: (index: number, updatedProblem: Problem) => void;
  onCancelEdit: () => void;
}

interface ValidationErrors {
  title?: string;
  question?: string;
  solution?: string;
  options?: string;
  correctAnswer?: string;
}

const EditProblem = ({
  problem,
  index,
  onUpdate,
  onCancelEdit,
}: EditProblemProps) => {
  const [editedProblem, setEditedProblem] = useState(problem);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!editedProblem.title?.trim()) {
      newErrors.title = "Title is required";
      isValid = false;
    }

    if (isHtmlContentEmpty(editedProblem.question)) {
      newErrors.question = "Question is required";
      isValid = false;
    }

    if (isHtmlContentEmpty(editedProblem.solution)) {
      newErrors.solution = "Solution is required";
      isValid = false;
    }

    if (isHtmlContentEmpty(editedProblem.correctAnswer)) {
      newErrors.correctAnswer = "Correct answer is required";
      isValid = false;
    }

    if (editedProblem.type === QuestionType.MultipleChoice) {
      if (
        !editedProblem.options?.length ||
        editedProblem.options.some((opt) => isHtmlContentEmpty(opt))
      ) {
        newErrors.options = "All options must be filled";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = () => {
    if (validateForm()) {
      onUpdate(index, editedProblem);
      onCancelEdit();
    }
  };

  const handleCancel = () => {
    setEditedProblem(problem);
    setErrors({});
    onCancelEdit();
  };

  const handleChange = (field: keyof Problem, value: any) => {
    setEditedProblem((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "type" && {
        options:
          value === QuestionType.MultipleChoice
            ? ["", "", "", ""]
            : value === QuestionType.TrueFalse
            ? ["True", "False"]
            : [],
      }),
    }));
    // Clear error for the field being changed
    if (errors[field as keyof ValidationErrors]) {
      setErrors((prev) => ({
 ...prev,
[field]: undefined 
}));
    }
  };

  return (
    <>
      <DialogContent
        sx={{
          px: 3,
          py: 2.5,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <EditingField
            label="Question"
            value={editedProblem.question}
            onChange={(value) => handleChange("question", value)}
            type="textarea"
            placeholder="Enter the question"
            isEditing={true}
            error={!!errors.question}
          />
          {errors.question && (
            <FormHelperText error>{errors.question}</FormHelperText>
          )}
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Question Type
          </Typography>
          <Select
            value={editedProblem.type}
            fullWidth
            onChange={(e) => handleChange("type", e.target.value)}
            size="small"
            sx={{
              borderRadius: 1.5,
            }}
          >
            <MenuItem value={QuestionType.MultipleChoice}>
              Multiple Choice
            </MenuItem>
            <MenuItem value={QuestionType.TrueFalse}>True/False</MenuItem>
            <MenuItem value={QuestionType.ShortAnswer}>Short Answer</MenuItem>
          </Select>
        </Box>

        <Box sx={{ mb: 2 }}>
          <QuestionContent
            type={editedProblem.type}
            problem={problem}
            isEditing={true}
            onChange={handleChange}
            editedProblem={editedProblem}
            errors={errors}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <EditingField
            label="Solution"
            value={editedProblem.solution}
            onChange={(value) => handleChange("solution", value)}
            type="textarea"
            placeholder="Enter the solution"
            isEditing={true}
            error={!!errors.solution}
          />
          {errors.solution && (
            <FormHelperText error>{errors.solution}</FormHelperText>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          py: 1.25,
          px: 3,
          borderTop: "1px solid",
          borderColor: "rgba(28, 72, 46, 0.45)",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button variant="contained" color="primary" onClick={handleSave} size="small">
          Save
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleCancel}
          size="small"
        >
          Cancel
        </Button>
      </DialogActions>
    </>
  );
};

export default EditProblem;
